import {OptionType} from 'platform/components';

import {find, head, isNil, isNotNil, uniqBy} from 'ramda';
import {isNilOrEmpty} from 'ramda-adjunct';

import {useGetCustomerV2Query, useGetVehicleCustomerQuery} from '@dms/api/customer';
import {useGetMetadaServiceCaseQuery, useGetServiceOrderQuery} from '@dms/api/workshop';
import {getNaturalPersonFullName} from '@dms/shared';

import {buildArray, Nullish} from 'shared/web';

interface UseGetDefaultCustomerOptionsProps {
  serviceCaseId: string;
  serviceOrderId?: string | Nullish;
}

export function useGetDefaultCustomerOptions(props: UseGetDefaultCustomerOptionsProps) {
  const {data: serviceOrder, isLoading: isLoadingServiceOrder} = useGetServiceOrderQuery(
    {serviceCaseId: props.serviceCaseId, serviceOrderId: props.serviceOrderId ?? ''},
    {skip: isNilOrEmpty(props.serviceOrderId)}
  );

  const {data: serviceCase, isLoading: isLoadingServiceCase} = useGetMetadaServiceCaseQuery(
    {serviceCaseId: props.serviceCaseId},
    {skip: isNilOrEmpty(props.serviceCaseId)}
  );

  const {data: vehicleCustomers, isLoading: isLoadingVehicleCustomers} = useGetVehicleCustomerQuery(
    {vehicleId: serviceCase?.vehicleId ?? ''},
    {skip: isNilOrEmpty(serviceCase?.vehicleId)}
  );

  const vehicleUser = find(
    (vehicleCustomer) => vehicleCustomer?.customerType === 'CORVEHICLECUSTOMERTYPE_USER',
    vehicleCustomers ?? []
  );

  const fleetManager = find(
    (vehicleCustomer) => vehicleCustomer?.customerType === 'CORVEHICLECUSTOMERTYPE_MANAGER',
    vehicleCustomers ?? []
  );

  const vehicleOwner = find(
    (vehicleCustomer) => vehicleCustomer?.customerType === 'CORVEHICLECUSTOMERTYPE_OWNER',
    vehicleCustomers ?? []
  );

  const vehicleOperator = find(
    (vehicleCustomer) => vehicleCustomer?.customerType === 'CORVEHICLECUSTOMERTYPE_OPERATOR',
    vehicleCustomers ?? []
  );

  const {data: driverCustomerData, isLoading: isLoadingDriverCustomerData} = useGetCustomerV2Query(
    {customerId: serviceOrder?.contactInformation?.id ?? ''},
    {skip: isNilOrEmpty(serviceOrder?.contactInformation?.id)}
  );

  const {data: vehicleUserData, isLoading: isLoadingVehicleUserData} = useGetCustomerV2Query(
    {customerId: vehicleUser?.customerId ?? ''},
    {skip: isNilOrEmpty(vehicleUser?.customerId)}
  );

  const {data: fleetManagerCustomerData, isLoading: isLoadingFleetManagerCustomerData} =
    useGetCustomerV2Query(
      {customerId: fleetManager?.customerId ?? ''},
      {skip: isNilOrEmpty(fleetManager?.customerId)}
    );

  const {data: vehicleOperatorCustomerData, isLoading: isLoadingVehicleOperatorCustomerData} =
    useGetCustomerV2Query(
      {customerId: vehicleOperator?.customerId ?? ''},
      {skip: isNilOrEmpty(vehicleOperator?.customerId)}
    );

  const {data: vehicleOwnerCustomerData, isLoading: isLoadingVehicleOwnerCustomerData} =
    useGetCustomerV2Query(
      {customerId: vehicleOwner?.customerId ?? ''},
      {skip: isNilOrEmpty(vehicleOwner?.customerId)}
    );

  const customers = uniqBy(
    (customer) => customer?.id,
    [
      driverCustomerData,
      vehicleUserData,
      fleetManagerCustomerData,
      vehicleOperatorCustomerData,
      vehicleOwnerCustomerData,
    ].filter(isNotNil)
  );

  const customerId = head(customers)?.id;

  const defaultCustomerOptions = buildArray<OptionType>();
  customers.forEach((customer) => {
    if (isNil(customer)) {
      return;
    }

    defaultCustomerOptions.push({
      label: getNaturalPersonFullName(customer.foundingPerson) ?? '',
      value: customer?.id ?? '',
    });
  });

  return {
    customerId,
    defaultCustomerOptions,
    isLoading:
      isLoadingServiceOrder ||
      isLoadingServiceCase ||
      isLoadingVehicleCustomers ||
      isLoadingDriverCustomerData ||
      isLoadingVehicleUserData ||
      isLoadingFleetManagerCustomerData ||
      isLoadingVehicleOperatorCustomerData ||
      isLoadingVehicleOwnerCustomerData,
  };
}
